<template>
  <div
    class="control-dial"
    :class="{ 'control-dial--active': active, 'control-dial--disabled': disabled }"
    :style="rootStyle">
    <!--
      The ring owns the dim gesture and nothing else: the SVG box is transparent
      to hits, only the stroked ring is a target (`pointer-events: stroke`), and
      `touch-action` on the box keeps a drag that starts on the ring from
      scrolling the sheet — while a touch anywhere else on the square (the hole,
      the corners) falls through to the page and scrolls as usual.
    -->
    <svg
      ref="ringEl"
      class="control-dial-ring"
      viewBox="0 0 100 100"
      :aria-hidden="true"
      @pointerdown="onRingDown"
      @pointermove="onRingMove"
      @pointerup="onRingUp"
      @pointercancel="onRingUp"
      @lostpointercapture="onRingUp">
      <circle class="control-dial-track" cx="50" cy="50" r="44" />
      <circle
        class="control-dial-arc"
        cx="50"
        cy="50"
        r="44"
        :stroke-dasharray="`${arcLength} ${CIRCUMFERENCE}`"
        :style="{ opacity: shown > 0 ? 1 : 0 }" />
      <!-- 26 units wide on a 24-unit radius band ≈ a 50 px finger target at the
           sheet's 196 px dial; `stroke: transparent` keeps it invisible. -->
      <circle class="control-dial-hit" cx="50" cy="50" r="44" />
    </svg>

    <button
      type="button"
      class="control-dial-knob"
      :disabled="disabled"
      :aria-label="label || undefined"
      @click="emit('toggle')">
      <!-- A device with a reading of its own (a thermostat's target and room
           temperature) prints those instead of the icon + `%`: the circle is the
           only place both numbers fit. -->
      <template v-if="mainText">
        <span class="control-dial-main">{{ mainText }}</span>
        <span v-if="subText" class="control-dial-sub">{{ subText }}</span>
      </template>
      <template v-else>
        <ControlIcon :name="icon ?? 'bulb'" size="1.9em" />
        <span v-if="showValue" class="control-dial-value">{{ shown }}<small>%</small></span>
        <span v-if="label" class="control-dial-label">{{ label }}</span>
      </template>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import { sheetAccentStyle } from './sheetAccent';

const props = withDefaults(
  defineProps<{
    /** Brightness, 0–100. */
    value: number;
    /** Lit while the device is on — the arc takes the accent, else it calms down. */
    active: boolean;
    disabled?: boolean;
    /** Overrides the sheet's accent. */
    accent?: string;
    /** The caption under the reading — and the knob's accessible name. */
    label?: string;
    /**
     * Whether the knob prints its `%` reading. A device with nothing to read
     * (a non-dimmable light) turns the line off rather than print a `0 %`.
     */
    showValue?: boolean;
    /** A reading to print instead of the `%` (a thermostat's target). */
    mainText?: string;
    /** The smaller line under it (the room's own temperature). */
    subText?: string;
    /** The glyph shown when there is no `mainText`. */
    icon?: string;
  }>(),
  { disabled: false, label: '', showValue: true, mainText: '', subText: '', icon: 'bulb' },
);

const emit = defineEmits<{
  /** A tap on the centre: the light's on/off. */
  toggle: [];
  /** A drag on the ring: the new brightness, already clamped to 0–100. */
  'set-value': [value: number];
}>();

/** The sketch's geometry: r=44 on a 100-box. */
const RING_RADIUS = 44;
const CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

const ringEl = ref<SVGSVGElement | null>(null);
const dragging = ref(false);
/**
 * The last value the drag produced — the arc and the reading follow it rather
 * than the prop, because a parent's update lands on the next render and the
 * ring must track the finger in this one.
 */
const lastValue = ref(props.value);

const rootStyle = computed(() => sheetAccentStyle(props.accent));

const shown = computed(() => (dragging.value ? lastValue.value : props.value));
const arcLength = computed(() => (Math.max(0, Math.min(100, shown.value)) / 100) * CIRCUMFERENCE);

/** The pointer's angle around the dial's centre → 0 % at 12 o'clock, clockwise. */
function valueFromPointer(event: PointerEvent): number {
  const el = ringEl.value;
  if (!el) return lastValue.value;
  const rect = el.getBoundingClientRect();
  const dx = event.clientX - (rect.left + rect.width / 2);
  const dy = event.clientY - (rect.top + rect.height / 2);
  const degrees = (Math.atan2(dy, dx) * 180) / Math.PI; // 0° = 3 o'clock, clockwise on screen
  const fromTop = (degrees + 90 + 360) % 360; // 0° = 12 o'clock
  return Math.round((fromTop / 360) * 100);
}

function apply(next: number) {
  const clamped = Math.max(0, Math.min(100, next));
  lastValue.value = clamped;
  if (clamped !== props.value) emit('set-value', clamped);
}

function onRingDown(event: PointerEvent) {
  if (props.disabled) return;
  // Capture: the gesture must survive the finger leaving the thin ring band.
  try {
    ringEl.value?.setPointerCapture(event.pointerId);
  } catch {
    /* noop */
  }
  dragging.value = true;
  apply(valueFromPointer(event));
}

function onRingMove(event: PointerEvent) {
  if (!dragging.value || props.disabled) return;
  apply(valueFromPointer(event));
}

function onRingUp() {
  if (!dragging.value) return;
  dragging.value = false;
}
</script>

<style scoped>
.control-dial {
  /* The sheet's accent, or the light accent when nothing has set one. */
  --dial-accent: var(--sheet-accent, var(--device-color-light));
  position: relative;
  width: 12.25rem;
  height: 12.25rem;
  margin: 0 auto;
  flex-shrink: 0;
}

.control-dial--disabled {
  opacity: 0.45;
}

.control-dial-ring {
  width: 100%;
  height: 100%;
  display: block;
  /* The box itself is inert: only the stroked circles below are targets, and a
     drag that starts on one of them must not scroll the sheet. */
  pointer-events: none;
  touch-action: none;
  transform: rotate(-90deg); /* the dash starts at 12 o'clock, not 3 */
}

.control-dial-track {
  fill: none;
  stroke: rgba(148, 163, 184, 0.28);
  stroke-width: 7;
  pointer-events: stroke;
}

.dark .control-dial-track {
  stroke: rgba(255, 255, 255, 0.08);
}

.control-dial-arc {
  fill: none;
  stroke: color-mix(in srgb, var(--dial-accent) 45%, transparent);
  stroke-width: 7;
  stroke-linecap: round;
  pointer-events: none;
  transition: stroke 0.2s;
}

.control-dial--active .control-dial-arc {
  stroke: var(--dial-accent);
}

/* The enlarged hit band (drawn last, so it is on top). */
.control-dial-hit {
  fill: none;
  stroke: transparent;
  stroke-width: 26;
  pointer-events: stroke;
}

.control-dial-knob {
  position: absolute;
  inset: 1.875rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.125rem;
  border-radius: 50%;
  border: 1px solid color-mix(in srgb, var(--dial-accent) 40%, rgba(148, 163, 184, 0.3));
  background: var(--icon-circle-fill);
  color: rgb(var(--gray-500));
  box-shadow: 0 0 24px color-mix(in srgb, var(--dial-accent) 18%, transparent);
  font: inherit;
  cursor: pointer;
  transition:
    color 0.2s,
    border-color 0.2s;
}

.dark .control-dial-knob {
  color: rgb(var(--gray-400));
}

.control-dial--active .control-dial-knob {
  color: var(--dial-accent);
}

.control-dial-knob:disabled {
  cursor: default;
}

.control-dial-value {
  font-size: 1.375rem;
  font-weight: 700;
  line-height: 1.1;
  color: inherit;
  font-variant-numeric: tabular-nums;
}

.control-dial-value small {
  margin-left: 0.15em;
  font-size: 0.875rem;
  font-weight: 600;
}

.control-dial-label {
  font-size: 0.625rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

/* The two readings a thermostat puts in the circle: the target large, the room
   small under it. */
.control-dial-main {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.05;
  color: inherit;
  font-variant-numeric: tabular-nums;
}

.control-dial-sub {
  font-size: 0.8125rem;
  font-weight: 600;
  line-height: 1.1;
  color: rgb(var(--gray-500));
  font-variant-numeric: tabular-nums;
}

.dark .control-dial-sub {
  color: rgb(var(--gray-400));
}

.dark .control-dial-label {
  color: rgb(var(--gray-400));
}
</style>
